import { ArrowDown, ArrowUpRight, Check, Copy, Moon, Sun } from "@phosphor-icons/react";
import { useEffect, useMemo, useState } from "react";
import { motion, useReducedMotion } from "motion/react";
import { TweetCarousel } from "@/components/TweetCarousel";
import { BagMaker } from "@/components/BagMaker";
import { TradeChooser } from "@/components/TradeChooser";
import { getDatastream } from "@/lib/datastream";

const TOKEN = "HcRLc9VDgjLeK154xDawfb1dmVJ98DoSqcwTHGqiDeJR";
const TOKEN_LOGO = "https://image.solanatracker.io/proxy?url=https%3A%2F%2Fgateway.irys.xyz%2F7uFsXwyuyzKDiqdda4L2cq3DcpcdRqYhwe4m8ZoVJ1BQ";

type Mechanics = {
  mode?: string;
  transferFeeBps?: number;
  quoteSymbol?: string;
  burns?: { amountTokens?: number; valueUsdAtBurn?: number; count?: number; lastAt?: string } | null;
  rewards?: { distributedTokens?: number; distributedUsd?: number; payoutCount?: number; holderCount?: number; lastAt?: string } | null;
  yield?: {
    dailyRate?: number;
    simpleApr?: number;
    compoundApy?: number;
    observed?: { positionUsd?: number; rewardUsd?: number; positionTokens?: number; rewardZec?: number; capturedAt?: string };
    distributedTokens24h?: number;
    eligibleSupplyEstimate?: number;
    windowHours?: number;
    paceUpdatedAt?: string;
    model?: string;
    zcatPriceUsd?: number;
    marketCapUsd?: number;
    circulatingSupply?: number;
    volume24hUsd?: number;
    quotePriceUsd?: number;
    solPriceUsd?: number;
    minHoldingUsd?: number;
    operatingFeeBps?: number;
  } | null;
  flywheel?: { active?: boolean; rank?: number; topCount?: number; weight?: number; sharePct?: number };
  generatedAt?: string;
};

type LiveStats = {
  priceUsd?: number;
  marketCapUsd?: number;
  distributedZec?: number;
};

type HolderGrowth = {
  total?: number;
  daily?: { added: number; percent: number } | null;
  weekly?: { added: number; percent: number } | null;
};

type AggregatedPriceUpdate = {
  price?: number;
  aggregated?: { median?: number; average?: number };
};

function compact(value?: number, money = true) {
  if (value === undefined || !Number.isFinite(value)) return "Syncing";
  const prefix = money ? "$" : "";
  if (value >= 1e9) return `${prefix}${(value / 1e9).toFixed(2)}B`;
  if (value >= 1e6) return `${prefix}${(value / 1e6).toFixed(2)}M`;
  if (value >= 1e3) return `${prefix}${(value / 1e3).toFixed(1)}K`;
  return `${prefix}${value.toLocaleString(undefined, { maximumFractionDigits: money ? 6 : 0 })}`;
}

function useMechanics() {
  const [data, setData] = useState<Mechanics>();
  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const response = await fetch("/api/mechanics", { cache: "no-store" });
        if (!response.ok) throw new Error();
        const result = await response.json();
        if (active) setData(result);
      } catch {
        // The explanation remains useful if StonkFun's live ledger is briefly unavailable.
      }
    };
    load();
    const timer = window.setInterval(load, 60_000);
    return () => { active = false; clearInterval(timer); };
  }, []);
  return data;
}

function useLiveStats(mechanics?: Mechanics): LiveStats {
  const [priceUsd, setPriceUsd] = useState<number>();
  const [distributedZec, setDistributedZec] = useState<number>();
  const fallbackPrice = mechanics?.yield?.zcatPriceUsd;
  const supply = mechanics?.yield?.circulatingSupply
    ?? (fallbackPrice && mechanics?.yield?.marketCapUsd ? mechanics.yield.marketCapUsd / fallbackPrice : undefined);

  useEffect(() => {
    const stream = getDatastream();
    const subscription = stream.subscribe.price.aggregated(TOKEN).on((update: AggregatedPriceUpdate) => {
      const nextPrice = Number(update.aggregated?.median ?? update.aggregated?.average ?? update.price);
      if (Number.isFinite(nextPrice) && nextPrice > 0) setPriceUsd(nextPrice);
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const response = await fetch("/api/rewards-live", { cache: "no-store" });
        if (!response.ok) throw new Error();
        const result = await response.json() as { distributedTokens?: number };
        if (active && Number.isFinite(result.distributedTokens)) setDistributedZec(result.distributedTokens);
      } catch {
        // Keep the last confirmed total during a brief upstream interruption.
      }
    };
    load();
    const timer = window.setInterval(load, 60_000);
    return () => { active = false; clearInterval(timer); };
  }, []);

  const currentPrice = priceUsd ?? fallbackPrice;
  return {
    priceUsd: currentPrice,
    marketCapUsd: currentPrice && supply ? currentPrice * supply : mechanics?.yield?.marketCapUsd,
    distributedZec: distributedZec ?? mechanics?.rewards?.distributedTokens,
  };
}

function useHolderGrowth() {
  const [data, setData] = useState<HolderGrowth>();
  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const response = await fetch("/api/holder-growth", { cache: "no-store" });
        if (!response.ok) throw new Error();
        const result = await response.json() as HolderGrowth;
        if (active) setData(result);
      } catch {
        // Preserve the layout while the live holder source reconnects.
      }
    };
    load();
    const timer = window.setInterval(load, 300_000);
    return () => { active = false; clearInterval(timer); };
  }, []);
  return data;
}

function ContractButton() {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(TOKEN);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };
  return <button className="contract" onClick={copy} aria-label="Copy ZCAT contract address"><span>CA</span>{TOKEN.slice(0, 5)}...{TOKEN.slice(-5)}{copied ? <Check size={16} /> : <Copy size={16} />}</button>;
}

export default function App() {
  const reduced = useReducedMotion();
  const [tradeOpen, setTradeOpen] = useState(false);
  const [zcatBalance, setZcatBalance] = useState("25000");
  const [balanceUnit, setBalanceUnit] = useState<"ZCAT" | "USD">("ZCAT");
  const [rewardHorizon, setRewardHorizon] = useState("30");
  const [rewardUnit, setRewardUnit] = useState<"days" | "weeks" | "months">("days");
  const [theme, setTheme] = useState<"Dark" | "Light">(() => {
    const saved = localStorage.getItem("zcat-theme");
    if (saved === "Light" || saved === "Dark") return saved;
    return window.matchMedia("(prefers-color-scheme: light)").matches ? "Light" : "Dark";
  });
  const mechanics = useMechanics();
  const live = useLiveStats(mechanics);
  const holderGrowth = useHolderGrowth();
  const enteredBalance = Math.max(0, Number(zcatBalance) || 0);
  const balanceNumber = balanceUnit === "USD"
    ? enteredBalance / (live.priceUsd || 1)
    : enteredBalance;
  const horizonNumber = Math.max(0, Number(rewardHorizon) || 0);
  const horizonDays = horizonNumber * (rewardUnit === "weeks" ? 7 : rewardUnit === "months" ? 30 : 1);
  const rewardModel = useMemo(() => {
    const live = mechanics?.yield;
    if (!live?.zcatPriceUsd || !live.marketCapUsd || !live.volume24hUsd || !live.quotePriceUsd) return undefined;
    const circulatingSupply = live.circulatingSupply || live.marketCapUsd / live.zcatPriceUsd;
    const positionValue = balanceNumber * live.zcatPriceUsd;
    const qualifies = positionValue >= (live.minHoldingUsd ?? 20);
    const taxRate = (mechanics?.transferFeeBps ?? 300) / 10_000;
    const operatingRate = (live.operatingFeeBps ?? 250) / 10_000;
    const netDailyPoolUsd = live.volume24hUsd * taxRate * (1 - operatingRate);
    const share = circulatingSupply > 0 ? balanceNumber / circulatingSupply : 0;
    const formulaZecPerDay = netDailyPoolUsd / live.quotePriceUsd;
    const calibration = live.distributedTokens24h && formulaZecPerDay > 0 ? live.distributedTokens24h / formulaZecPerDay : 1;
    const payoutZec = qualifies ? formulaZecPerDay * calibration * share * horizonDays : 0;
    return { positionValue, qualifies, netDailyPoolUsd, calibration, payoutZec, payoutUsd: payoutZec * live.quotePriceUsd };
  }, [balanceNumber, horizonDays, mechanics]);

  const switchBalanceUnit = () => {
    const zcatPrice = live.priceUsd;
    if (!zcatPrice) return;
    const nextUnit = balanceUnit === "ZCAT" ? "USD" : "ZCAT";
    const nextValue = balanceUnit === "ZCAT" ? enteredBalance * zcatPrice : enteredBalance / zcatPrice;
    setZcatBalance(nextValue.toFixed(2).replace(/\.0+$/, ""));
    setBalanceUnit(nextUnit);
  };

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "Dark");
    document.documentElement.dataset.theme = theme.toLowerCase();
    localStorage.setItem("zcat-theme", theme);
  }, [theme]);

  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  useEffect(() => {
    document.title = path === "/about" ? "About ZCAT | The Bag Stays On" : path === "/mechanics" ? "How ZCAT Works | The Bag Stays On" : "ZCAT | The Bag Stays On";
  }, [path]);
  const navigation = (
    <nav className="nav-shell" aria-label="Main navigation">
      <a className="wordmark" href="/" aria-label="ZCAT home"><img className="coin-logo" src={TOKEN_LOGO} width="36" height="36" alt="" decoding="async" />ZCAT</a>
      <div className="nav-links"><a href="/#rewards">Rewards</a><a href="/about">About</a></div>
      <div className="nav-actions">
        <button className="theme-toggle" onClick={() => setTheme(theme === "Dark" ? "Light" : "Dark")} aria-label={`Switch to ${theme === "Dark" ? "light" : "dark"} mode`} title={`Switch to ${theme === "Dark" ? "light" : "dark"} mode`}>{theme === "Dark" ? <Sun size={19} weight="bold" /> : <Moon size={19} weight="bold" />}</button>
        <button className="button button-primary nav-buy" onClick={() => setTradeOpen(true)}>Buy ZCAT <ArrowUpRight size={17} weight="bold" /></button>
      </div>
      <div className="mobile-nav-strip">
        <a href="/#rewards">Rewards</a><a href="/about">About</a><a href="/#maker">Your bag</a>
        <span><i>MC</i><b>{compact(live.marketCapUsd)}</b></span>
        <span><i>Price</i><b>{compact(live.priceUsd)}</b></span>
        <span><i>ZEC paid</i><b>{live.distributedZec === undefined ? "—" : compact(live.distributedZec, false)}</b></span>
      </div>
    </nav>
  );
  const footer = (
    <footer className="site-footer">
      <div><a className="wordmark" href="/" aria-label="ZCAT home"><img className="coin-logo" src={TOKEN_LOGO} width="36" height="36" alt="" decoding="async" />ZCAT</a><p>A private cat in a very public market.</p></div>
      <div className="footer-links"><a href="/about">About</a><a href="/mechanics">How it works</a><a href={`https://solscan.io/token/${TOKEN}`} target="_blank" rel="noreferrer">Solscan</a><a href="https://x.com/search?q=%24ZCAT" target="_blank" rel="noreferrer">X</a><button onClick={() => setTradeOpen(true)}>Buy ZCAT</button></div>
      <p className="legal">ZCAT is highly speculative. Nothing here is financial advice. Verify the contract and do your own research.</p>
    </footer>
  );

  if (path === "/about") return (
    <main className="inner-page">
      {navigation}
      <section className="thesis section about page-section" aria-labelledby="about-title">
        <div className="thesis-intro"><p className="eyebrow">About ZCAT</p><h1 id="about-title">Cat. Bag. ZEC.</h1><p>ZCAT turns privacy culture into a meme anyone can wear.</p></div>
        <div className="about-story">
          <article><span>01</span><h2>The meme</h2><p>A cat in a paper bag. Anonymous, recognizable, and built for profile pictures.</p></article>
          <article><span>02</span><h2>The reward</h2><p>ZCAT transfers fund automatic ZEC distributions to eligible holders.</p></article>
          <article><span>03</span><h2>The loop</h2><p>A separate flywheel buys and burns ZCAT while it qualifies.</p></article>
        </div>
        <div className="about-facts">
          <div><span>ZCAT price</span><strong>{compact(live.priceUsd)}</strong></div>
          <div><span>24h volume</span><strong>{compact(mechanics?.yield?.volume24hUsd)}</strong></div>
          <div><span>ZEC price</span><strong>{compact(mechanics?.yield?.quotePriceUsd)}</strong></div>
          <div><span>Minimum</span><strong>{compact(mechanics?.yield?.minHoldingUsd ?? 20)}</strong></div>
        </div>
        <div className="reward-deep" aria-labelledby="reward-system-title">
          <div className="reward-deep-head"><p className="eyebrow">The reward system</p><h2 id="reward-system-title">How holding ZCAT earns ZEC.</h2><p>Rewards happen automatically. No staking and no claim button.</p></div>
          <div className="reward-steps">
            <details><summary><span>01</span><strong>ZCAT moves. The tax builds.</strong></summary><p>ZCAT uses Token-2022 with a 3% transfer tax. When ZCAT moves through a buy, sell, or wallet transfer, the token program withholds part of that movement for the reward system.</p></details>
            <details><summary><span>02</span><strong>The reward pool becomes ZEC.</strong></summary><p>StonkFun collects the withheld ZCAT, applies its current 2.5% operating deduction, and converts the distributable value into ZEC. Its live ledger currently reports a $50,000 distribution threshold.</p></details>
            <details><summary><span>03</span><strong>Eligible holders share pro rata.</strong></summary><p>Wallets worth at least $20 at the eligibility snapshot share the ZEC pool according to their ZCAT balance. A larger eligible balance receives a larger share. The current minimum individual payout is $0.001.</p></details>
            <details><summary><span>04</span><strong>ZEC lands automatically.</strong></summary><p>Eligible wallets receive ZEC directly. There is no staking vault, lockup, or manual claim. Timing and amount change with transfer activity, conversion execution, wallet balance, and distribution thresholds.</p></details>
            <details><summary><span>05</span><strong>The calculator follows real payouts.</strong></summary><p>Our estimate uses the measured ZEC distribution pace, your share of circulating supply, and your chosen time. The ledger is sampled every five minutes and becomes a rolling 24-hour window. It is a live estimate—not a guaranteed return.</p></details>
          </div>
        </div>
        <div className="burn-explainer">
          <div><p className="eyebrow">Separate system</p><h2>Buyback and burn.</h2></div>
          <p>The reward tax pays holders. The flywheel is different: 5% of StonkFun V3 pool trading fees funds buybacks across its top 10 tokens, weighted by market cap. While ZCAT qualifies, its allocation buys ZCAT and permanently burns it. One system distributes ZEC; the other reduces ZCAT supply.</p>
        </div>
        <div className="mechanics-sources about-sources"><span>Verify it</span><a href="https://www.stonkfun.xyz/token/HcRLc9VDgjLeK154xDawfb1dmVJ98DoSqcwTHGqiDeJR" target="_blank" rel="noreferrer">Launch record <ArrowUpRight size={13} /></a><a href="https://www.stonkfun.xyz/rewards" target="_blank" rel="noreferrer">Rewards <ArrowUpRight size={13} /></a><a href="https://www.stonkfun.xyz/flywheel" target="_blank" rel="noreferrer">Flywheel <ArrowUpRight size={13} /></a><a href={`https://solscan.io/token/${TOKEN}`} target="_blank" rel="noreferrer">Solscan <ArrowUpRight size={13} /></a></div>
      </section>
      {footer}<TradeChooser open={tradeOpen} onClose={() => setTradeOpen(false)} />
    </main>
  );

  if (path === "/mechanics") return (
    <main className="inner-page">
      {navigation}
      <section className="economics section page-section" aria-labelledby="economics-title">
        <div className="economics-head"><div><p className="eyebrow">The engine room</p><h1 id="economics-title">How it works.</h1></div><p>Rewards and burns are separate. Both are live and auditable.</p></div>
        <div className="ledger" aria-label="Live ZCAT mechanics ledger">
          <article><span>ZEC distributed</span><strong>{live.distributedZec === undefined ? "Syncing" : `${live.distributedZec.toLocaleString(undefined, { maximumFractionDigits: 2 })} ZEC`}</strong><small>{mechanics?.rewards?.payoutCount?.toLocaleString() || "—"} payouts · {mechanics?.rewards?.holderCount?.toLocaleString() || "—"} rewarded wallets</small></article>
          <article><span>ZCAT permanently burned</span><strong>{mechanics?.burns?.amountTokens === undefined ? "Syncing" : `${compact(mechanics.burns.amountTokens, false)} ZCAT`}</strong><small>{mechanics?.burns?.count?.toLocaleString() || "—"} verified burns</small></article>
          <article><span>Flywheel position</span><strong>{mechanics?.flywheel?.active ? `#${mechanics.flywheel.rank} of ${mechanics.flywheel.topCount}` : mechanics ? "Not active" : "Syncing"}</strong><small>{mechanics?.flywheel?.weight === undefined ? "Market-cap weighted" : `${(mechanics.flywheel.weight * 100).toFixed(1)}% of the current tick`}</small></article>
        </div>
        <div className="mechanics-flow">
          <article><span>01 / Transfer</span><h2>Every move pays 3%.</h2><p>The Token-2022 transfer tax collects ZCAT as it moves.</p></article>
          <article><span>02 / Reward</span><h2>The tax becomes ZEC.</h2><p>After the operating deduction, eligible holders share ZEC pro rata. No staking. No claim button.</p></article>
          <article><span>03 / Buyback</span><h2>The flywheel buys ZCAT.</h2><p>A separate slice of V3 pool fees funds top-10 token buybacks.</p></article>
          <article><span>04 / Burn</span><h2>Bought ZCAT disappears.</h2><p>Flywheel purchases are permanently burned while ZCAT remains eligible.</p></article>
        </div>
        <details className="about-details mechanics-details"><summary>How the estimate works</summary><p>Live ZEC payout pace × your share of supply × time. The rolling distribution ledger currently adjusts the raw volume formula by {rewardModel ? `${rewardModel.calibration.toFixed(2)}×` : "a live factor"}. It is an estimate, not a promised payout.</p></details>
        <div className="mechanics-sources"><span>Primary records</span><a href="https://www.stonkfun.xyz/token/HcRLc9VDgjLeK154xDawfb1dmVJ98DoSqcwTHGqiDeJR" target="_blank" rel="noreferrer">Launch record <ArrowUpRight size={13} /></a><a href="https://www.stonkfun.xyz/rewards" target="_blank" rel="noreferrer">Rewards <ArrowUpRight size={13} /></a><a href="https://www.stonkfun.xyz/flywheel" target="_blank" rel="noreferrer">Flywheel <ArrowUpRight size={13} /></a><a href={`https://solscan.io/token/${TOKEN}`} target="_blank" rel="noreferrer">Solscan <ArrowUpRight size={13} /></a></div>
      </section>
      {footer}<TradeChooser open={tradeOpen} onClose={() => setTradeOpen(false)} />
    </main>
  );

  return (
    <main>
      {navigation}

      <section className="hero" id="top">
        <div className="hero-art" aria-hidden="true"><img src="/assets/zcat-hero.png" alt="" /></div>
        <motion.div className="hero-copy" initial={reduced ? false : { opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .65 }}>
          <p className="signal"><span /> Privacy has a mascot</p>
          <h1>THE BAG<br /><i>STAYS</i> ON.</h1>
          <p className="hero-body">A private cat. A paper bag. ZEC rewards.</p>
          <div className="hero-actions"><button className="button button-primary" onClick={() => setTradeOpen(true)}>Get the bag <ArrowUpRight size={19} weight="bold" /></button><a className="button button-quiet" href="#rewards">Rewards <ArrowDown size={18} /></a></div>
          <ContractButton />
        </motion.div>
        <div className="hero-stats" aria-label="Live ZCAT statistics">
          <div><span>Market cap</span><strong>{compact(live.marketCapUsd)}</strong></div>
          <div><span>Price</span><strong>{compact(live.priceUsd)}</strong></div>
          <div><span>ZEC distributed</span><strong>{live.distributedZec === undefined ? "Syncing" : compact(live.distributedZec, false)}</strong></div>
        </div>
      </section>

      <section className="rewards-section section" id="rewards" aria-labelledby="rewards-title">
        <div className="rewards-heading">
          <div><p className="eyebrow"><span className="live-dot" /> Live rewards</p><h2 id="rewards-title">Your ZEC estimate.</h2></div>
          <p>Balance in. ZEC estimate out. No wallet connect.</p>
        </div>
        <div className="rewards-calculator">
          <div className="rewards-inputs">
            <label htmlFor="zcat-balance">Your position</label>
            <div className="token-input"><input id="zcat-balance" inputMode="decimal" type="number" min="0" step="any" value={zcatBalance} onChange={(event) => setZcatBalance(event.target.value)} /><button className="balance-unit" type="button" onClick={switchBalanceUnit} aria-label={`Switch amount unit. Current unit ${balanceUnit}`}>{balanceUnit}</button></div>
            <label htmlFor="reward-horizon">Estimate over</label>
            <div className="horizon-input"><input id="reward-horizon" inputMode="numeric" type="number" min="0" step="1" value={rewardHorizon} onChange={(event) => setRewardHorizon(event.target.value)} /><div className="unit-switch" role="group" aria-label="Time unit">{(["days", "weeks", "months"] as const).map((unit) => <button key={unit} type="button" className={rewardUnit === unit ? "active" : ""} onClick={() => setRewardUnit(unit)}>{unit}</button>)}</div></div>
            <div className={`eligibility ${rewardModel?.qualifies ? "eligible" : "ineligible"}`}><span />{rewardModel ? rewardModel.qualifies ? `Eligible · position ≈ ${compact(rewardModel.positionValue)}` : `Below the ${compact(mechanics?.yield?.minHoldingUsd ?? 20)} minimum · position ≈ ${compact(rewardModel.positionValue)}` : "Syncing live eligibility"}</div>
          </div>
          <div className="rewards-output" aria-live="polite">
            <span>Estimated ZEC reward</span>
            <strong>{rewardModel ? rewardModel.payoutZec.toLocaleString(undefined, { maximumFractionDigits: 6 }) : "—"}<em>ZEC</em></strong>
            <p>{rewardModel ? `≈ ${rewardModel.payoutUsd.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 })} over ${horizonNumber.toLocaleString()} ${rewardUnit}` : "Loading current StonkFun market data…"}</p>
            <small>Illustrative estimate, not a ledger balance or promised payout.</small>
          </div>
        </div>
      </section>

      <BagMaker holderGrowth={holderGrowth} />
      <TweetCarousel />
      {footer}
      <TradeChooser open={tradeOpen} onClose={() => setTradeOpen(false)} />
    </main>
  );
}
